type Rgba = `rgba(${number},${number},${number},${number})`;

/**
 * Converts a hex color value to an RGBA string.
 * @function hexToRgba
 * @param {string | undefined} value - The hex color value.
 * @returns {Rgba} The RGBA string.
 * @throws {Error} If the hex color value is invalid.
 */
const hexToRgba = (value: string | undefined): Rgba => {
  if (!value || typeof value !== 'string') {
    throw new Error('Invalid hex color');
  }

  value = value.trim().replace(/\s/g, '').replace(/^#/, '');

  if (value.startsWith('rgba')) return value as Rgba;
  if (value.startsWith('rgb'))
    throw new Error('Invalid format: Use rgba instead');

  let r = 0,
    g = 0,
    b = 0,
    a = 1; // Default values

  if (value.length === 3) {
    if (!/^[0-9a-fA-F]{3}$/.test(value)) throw new Error('Invalid hex color');
    const v0 = value[0]!;
    const v1 = value[1]!;
    const v2 = value[2]!;
    r = parseInt(v0 + v0, 16);
    g = parseInt(v1 + v1, 16);
    b = parseInt(v2 + v2, 16);
  } else if (value.length === 6) {
    if (!/^[0-9a-fA-F]{6}$/.test(value)) throw new Error('Invalid hex color');
    r = parseInt(value.substring(0, 2), 16);
    g = parseInt(value.substring(2, 4), 16);
    b = parseInt(value.substring(4, 6), 16);
  } else if (value.length === 8) {
    if (!/^[0-9a-fA-F]{8}$/.test(value)) throw new Error('Invalid hex color');
    r = parseInt(value.substring(0, 2), 16);
    g = parseInt(value.substring(2, 4), 16);
    b = parseInt(value.substring(4, 6), 16);
    a = parseInt(value.substring(6, 8), 16) / 255;
  } else {
    throw new Error('Invalid hex color');
  }

  return `rgba(${r},${g},${b},${a.toFixed(2)})` as Rgba;
};

/**
 * Converts an RGB color value to an RGBA string.
 * @function rbgToRgba
 * @param {string | undefined} value - The RGB color value.
 * @returns {Rgba} The RGBA string.
 * @throws {Error} If the RGB color value is invalid.
 */
const rbgToRgba = (value: string | undefined): Rgba => {
  value = (value || '').trim().replace(/\s/g, '');

  if (!value.startsWith('rgba')) {
    value = value.replace('rgb', 'rgba').replace(')', ',1)');
  }

  if (!value.startsWith('rgba')) throw new Error('invalid rgb color');

  return value as Rgba;
};

/**
 * Parses a color value and returns an object representing the RGBA components.
 * @function parseColor
 * @param {string | undefined} value - The color value to parse.
 * @returns {object} An object representing the RGBA components.
 * @throws {Error} If the color value is invalid.
 */
const parseColor = (value: string | undefined) => {
  value = (value || '').trim().replace(/\s/g, '').replace(';', '');

  if (!value) throw new Error('invalid color');

  const rgba = {
    r: 0,
    g: 0,
    b: 0,
    a: 0,
    toString: function () {
      return `rgba(${this.r},${this.g},${this.b},${this.a < 1 ? this.a.toFixed(2) : this.a})` as Rgba;
    },
  };

  if (value === 'transparent') return rgba;

  if (value.startsWith('#')) {
    value = hexToRgba(value);
  }

  if (!value.startsWith('rgba')) {
    value = rbgToRgba(value);
  }

  if (!value.startsWith('rgba')) throw new Error('invalid rgba color');

  const parts = value
    .replace('rgba', '')
    .replace(/[\(\)]+/g, '')
    .split(',');

  if (parts.length !== 4) throw new Error('invalid rgba length');

  rgba.r = Number(parts[0]);
  rgba.g = Number(parts[1]);
  rgba.b = Number(parts[2]);
  rgba.a = Number(parts[3]);

  return rgba;
};

export { hexToRgba, rbgToRgba, parseColor };
